export interface User {
  id: string;
  _id?: string;
  username: string;
  email?: string;
  // Backend returns displayName/arabicName, not firstName/lastName
  displayName?: string;
  arabicName?: string;
  // Computed fields for backward compatibility
  firstName?: string;
  lastName?: string;
  fullName?: string;
  role: UserRole;
  roleLevel?: number;
  roles?: Role[];
  permissions: Permission[] | string[];
  allowedOutlets?: string[];
  cocode: string | number;
  language?: 'ar' | 'en';
  outletCode?: string;
  isActive?: boolean;
  mustChangePassword?: boolean;
  createdAt?: string;
  updatedAt?: string;
  lastLogin?: string;
}

export type UserRole = 'superadmin' | 'admin' | 'manager' | 'user' | 'viewer';

// Specific known permissions
export type KnownPermission =
  // Sales
  | 'sales:create'
  | 'sales:read'
  | 'sales:update'
  | 'sales:void'
  | 'sales:return'
  // Purchases
  | 'purchases:create'
  | 'purchases:read'
  | 'purchases:update'
  | 'purchases:void'
  | 'purchases:return'
  // Inventory
  | 'inventory:read'
  | 'inventory:adjust'
  | 'inventory:transfer'
  | 'inventory:stocktake'
  // Products
  | 'products:create'
  | 'products:read'
  | 'products:update'
  | 'products:delete'
  | 'items:read'
  | 'items:write'
  | 'items:delete'
  // Reports
  | 'reports:read'
  | 'reports:export'
  | 'reports:advanced'
  // Users
  | 'users:create'
  | 'users:read'
  | 'users:update'
  | 'users:delete'
  | 'users:manage'
  | 'users:write'
  // Roles
  | 'roles:read'
  | 'roles:manage'
  | 'roles:write'
  | 'roles:delete'
  // Settings
  | 'settings:read'
  | 'settings:update'
  | 'settings:write'
  // Partners
  | 'partners:create'
  | 'partners:read'
  | 'partners:update'
  | 'partners:delete'
  // Outlets
  | 'outlets:read'
  | 'outlets:manage'
  // Dashboard
  | 'dashboard:read'
  | 'dashboard:alerts'
  // Invoices (legacy)
  | 'invoices:read'
  | 'invoices:write'
  | 'invoices:delete'
  | 'invoices:post';

// Wildcard permission patterns
export type WildcardPermission =
  | '*:*'
  | 'users:*'
  | 'items:*'
  | 'invoices:*'
  | 'reports:*'
  | 'settings:*'
  | 'roles:*'
  | 'sales:*'
  | 'purchases:*'
  | 'inventory:*'
  | 'products:*'
  | 'partners:*'
  | 'outlets:*'
  | 'dashboard:*';

// Permission type includes both specific and wildcard patterns
export type Permission = KnownPermission | WildcardPermission;

export interface Role {
  _id: string;
  id?: number;
  name: string;
  displayNameEn: string;
  displayNameAr: string;
  level: number;
  permissions: Permission[];
  isActive: boolean;
  description?: string;
  createdAt?: string;
  updatedAt?: string;
}

export interface LoginCredentials {
  username: string;
  password: string;
  cocode?: number;
}

export interface Company {
  cocode: number;
  name: string;
  nameAr: string;
}

export interface LoginResponse {
  user: User;
  accessToken: string;
  refreshToken: string;
  expiresIn: number;
}

export interface RefreshTokenResponse {
  accessToken: string;
  refreshToken?: string;
  expiresIn: number;
}

export interface AuthState {
  user: User | null;
  accessToken: string | null;
  isAuthenticated: boolean;
  isLoading: boolean;
}

export interface CreateUserData {
  username: string;
  email: string;
  password: string;
  firstName: string;
  lastName: string;
  role: UserRole;
  roles?: string[];
  cocode?: string;
  outletCode?: string;
  isActive?: boolean;
}

export interface UpdateUserData {
  username?: string;
  email?: string;
  firstName?: string;
  lastName?: string;
  role?: UserRole;
  roles?: string[];
  cocode?: string;
  outletCode?: string;
  isActive?: boolean;
}

export interface ChangePasswordData {
  currentPassword?: string;
  newPassword: string;
  confirmPassword: string;
}

export interface CreateRoleData {
  name: string;
  displayNameEn: string;
  displayNameAr: string;
  level: number;
  permissions: Permission[];
  description?: string;
  isActive?: boolean;
}

export interface UpdateRoleData {
  name?: string;
  displayNameEn?: string;
  displayNameAr?: string;
  level?: number;
  permissions?: Permission[];
  description?: string;
  isActive?: boolean;
}

// Permission groups for better UI organization
export const PERMISSION_GROUPS = {
  users: {
    labelEn: 'User Management',
    labelAr: 'إدارة المستخدمين',
    permissions: ['users:read', 'users:write', 'users:delete'] as Permission[],
  },
  roles: {
    labelEn: 'Role Management',
    labelAr: 'إدارة الأدوار',
    permissions: ['roles:read', 'roles:write', 'roles:delete'] as Permission[],
  },
  items: {
    labelEn: 'Item Management',
    labelAr: 'إدارة الأصناف',
    permissions: ['items:read', 'items:write', 'items:delete'] as Permission[],
  },
  invoices: {
    labelEn: 'Invoice Management',
    labelAr: 'إدارة الفواتير',
    permissions: ['invoices:read', 'invoices:write', 'invoices:delete', 'invoices:post'] as Permission[],
  },
  reports: {
    labelEn: 'Reports',
    labelAr: 'التقارير',
    permissions: ['reports:read', 'reports:export'] as Permission[],
  },
  settings: {
    labelEn: 'Settings',
    labelAr: 'الإعدادات',
    permissions: ['settings:read', 'settings:write'] as Permission[],
  },
} as const;

export type PermissionGroup = keyof typeof PERMISSION_GROUPS;
